import { z } from "zod";
import { InvoiceStatus } from "@/gql/graphql";
import { createRoute, Outlet } from "@tanstack/react-router";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { InvoiceRecordScreen } from "@/app/Invoices/Screens/InvoiceRecordScreen";
import { InvoiceDetailScreen } from "@/app/Invoices/Screens/InvoiceDetailScreen";
import { InvoiceActivityScreen } from "@/app/Invoices/Screens/InvoiceActivityScreen";
import { InvoiceCreateScreen } from "@/app/Invoices/Screens/InvoiceCreateScreen";
import { appLayoutRoute } from "@/routes/layout-routes";
import { getInvoiceListQueryVariables } from "@/app/Invoices/Utils/getInvoiceListQueryVariables";
import {
  fetchQuery,
  prefetchInfiniteQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { invoiceListQuery } from "@/app/Invoices/GraphQL/invoiceListQuery";
import { supplierParamToId } from "@/app/Suppliers/Utils/globalSupplier";
import { invoiceRecordQuery } from "@/app/Invoices/GraphQL/invoiceRecordQuery";
import { InvoiceTableScreen } from "@/app/Invoices/Screens/InvoiceTableScreen";
import { InvoiceListScreen } from "@/app/Invoices/Screens/InvoiceListScreen";

const invoiceListSearchParams = z.object({
  invoiceStatus: fallback(
    z.array(z.enum(InvoiceStatus)).optional(),
    undefined,
  ).default(undefined),
});

export type InvoiceListSearchParams = z.infer<typeof invoiceListSearchParams>;

export const invoicesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/invoices",
  component: Outlet,
  validateSearch: zodValidator(invoiceListSearchParams),
  head: () => ({
    meta: [
      {
        title: "Invoices - Imoova Admin",
      },
    ],
  }),
});

export const invoiceCreateRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: "/create",
  component: InvoiceCreateScreen,
  head: () => ({
    meta: [
      {
        title: "Create Invoice - Imoova Admin",
      },
    ],
  }),
});

export const invoiceTableRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  path: "/",
  component: InvoiceTableScreen,
  validateSearch: zodValidator(invoiceListSearchParams),
  loaderDeps: ({ search: { invoiceStatus } }) => ({
    invoiceStatus,
  }),
  head: () => ({
    meta: [
      {
        title: "Invoices - Imoova Admin",
      },
      {
        name: "description",
        content: "Manage invoices",
      },
    ],
  }),
});

export const invoiceRecordLayoutRoute = createRoute({
  getParentRoute: () => invoicesRoute,
  id: "_invoiceRecordLayout",
  component: InvoiceListScreen,
  loader: ({ deps, params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    return prefetchInfiniteQuery(
      invoiceListQuery,
      getInvoiceListQueryVariables(deps, supplierId),
      params.orgId,
    );
  },
});

export const invoiceRoute = createRoute({
  getParentRoute: () => invoiceRecordLayoutRoute,
  path: "$id",
  component: InvoiceRecordScreen,
  loader: ({ params }) => {
    return fetchQuery(invoiceRecordQuery, { id: params.id }, params.orgId);
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.invoice.reference ?? "Invoice"} - Imoova Admin`,
      },
    ],
  }),
});

export const invoiceDetailRoute = createRoute({
  getParentRoute: () => invoiceRoute,
  path: "/",
  component: InvoiceDetailScreen,
});

export const invoiceActivityRoute = createRoute({
  getParentRoute: () => invoiceRoute,
  path: "/activity",
  component: InvoiceActivityScreen,
});
